import { all, actionChannel, takeLatest, takeEvery, put, call, select, take } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { t } from 'src/i18n';
import { RootState } from 'src/redux/store';
import notifications from 'src/redux/notifications/actions';
import { createCard, downloadTemplate, uploadSide } from '../api';
import { triggerDownload } from '../download';
import { CSV_CONTENT_TYPE, RATE_CARD_HTTP } from '../constants';
import actions from './actions';
import actionTypes from './actionTypes';
import type { CreateRequestAction, DownloadTemplateRequestAction, UploadSideRequestAction } from './actions.d';
import type { ICreateCardPayload } from '../types';

// `sendRequest` never re-throws, so a `catch` here would never fire: with `preserveErrorStatus` a
// body-carrying failure arrives resolved, while 401/403/404 come back `undefined` — a generic failure.
const validationsOf = (result: any): Record<string, string[]> | null =>
  result && typeof result === 'object' && result.validations ? result.validations : null;

const isSuccess = (result: any): boolean =>
  typeof result?.status === 'number' && result.status >= 200 && result.status < 300;

/**
 * `base` first: the whole-file refusals — a header mismatch, colliding or unconfigured columns, no
 * data rows, an unparseable CSV. None is fixed by picking another file, so the panel banners them.
 */
const uploadFailure = (validations: Record<string, string[]> | null) => {
  const baseMessage = validations?.base?.[0];

  if (baseMessage) return { message: baseMessage, prominent: true };

  const fieldMessage = validations ? Object.values(validations).flat()[0] : undefined;

  return { message: fieldMessage ?? t('dash.common.notification.something_went_wrong'), prominent: false };
};

/**
 * `preserveErrorStatus` is what makes the 409 branch below reachable: without it a failure carries no
 * status, so a publish-in-progress refusal is indistinguishable from a bad file.
 */
const UPLOAD_REQUEST_OPTIONS = { preserveErrorStatus: true };

/**
 * Resolves the card this upload attaches to, minting it first if this is the wizard's first file. The
 * shell waits for a file because there is no delete endpoint, and two concurrent sides share one card.
 */
export function* ensureCardId(details?: ICreateCardPayload): Generator<any, string | null, any> {
  const { cardId, submitting } = yield select((state: RootState) => state.RateCardCreate);

  if (cardId) return cardId;

  // Unreachable by contract, so a defect rather than something to explain to an administrator —
  // logged, because the caller can only render it as one more failed upload.
  if (!details) {
    logger.error(new Error('rate card upload dispatched with neither a card id nor details'));

    return null;
  }

  // Armed before the create is asked for: `createRequest` can put its outcome without ever suspending,
  // and a bare `take` would park forever. `CLEAR_STORE` counts — a discarded wizard keeps its file.
  const outcomes = yield actionChannel([actionTypes.CREATE_SUCCESS, actionTypes.CREATE_ERROR, actionTypes.CLEAR_STORE]);

  try {
    if (!submitting) yield put(actions.createRequest(details));

    const outcome = yield take(outcomes);

    return outcome.type === actionTypes.CREATE_SUCCESS ? outcome.payload.cardId : null;
  } finally {
    outcomes.close();
  }
}

export function* uploadSideRequest({ payload }: UploadSideRequestAction): Generator<any, void, any> {
  const { side, file, token } = payload;

  try {
    // Annotated, because `yield` is typed `any`: without it the declared return type checks nothing.
    const cardId: string | null = payload.cardId ?? (yield call(ensureCardId, payload.details));

    // No card to upload to, so the file is kept and the failure marked retryable. A 422 left
    // `validations` and the wizard bounces to Details; a transport failure left none to act on.
    if (!cardId) {
      const { validations } = yield select((state: RootState) => state.RateCardCreate);

      yield put(
        actions.uploadSideError({
          side,
          token,
          message: t(
            validations
              ? 'dash.admin.rateCards.editor.import.errors.create_failed_details'
              : 'dash.admin.rateCards.editor.import.errors.create_failed',
          ),
          retryable: true,
        }),
      );

      return;
    }

    const body = new FormData();
    body.append('upload[side]', side);
    // Re-labelled, not re-encoded: Windows with Excel reports a `.csv` as `application/vnd.ms-excel`,
    // which the form refuses. The bytes are untouched, so the server's own spoof check still applies.
    body.append('upload[spreadsheet]', new File([file], file.name, { type: CSV_CONTENT_TYPE }));

    // No errorType: a rejected file names the offending column, shown inline on its own side.
    const result = yield call(
      sendRequest,
      uploadSide,
      { data: body, id: cardId },
      undefined,
      undefined,
      undefined,
      UPLOAD_REQUEST_OPTIONS,
    );

    if (isSuccess(result)) {
      const uploadId: string | undefined = result.data?.id;

      if (uploadId) {
        yield put(actions.uploadSideSuccess({ side, token, uploadId }));

        return;
      }

      // Without the id the row can never be matched to the card's verdict on it, so nothing could ever
      // settle it — a contract break worth hearing about, and shown as a failed upload to retry.
      logger.error(new Error(`rate card upload for ${side} answered ${result.status} without an id`));
      yield put(
        actions.uploadSideError({
          side,
          token,
          message: t('dash.admin.rateCards.editor.import.state_failed'),
          retryable: true,
        }),
      );

      return;
    }

    // A 409 means a publish is running, not that the file is wrong — it is worth retrying as-is.
    if (result?.status === RATE_CARD_HTTP.CONFLICT) {
      yield put(
        actions.uploadSideError({
          side,
          token,
          message: t('dash.admin.rateCards.editor.import.errors.publish_in_progress'),
          retryable: true,
        }),
      );

      return;
    }

    yield put(actions.uploadSideError({ side, token, ...uploadFailure(validationsOf(result)) }));
  } catch (error) {
    logger.error(error);
    yield put(actions.uploadSideError({ side, token, message: t('dash.common.notification.something_went_wrong') }));
  }
}

export function* downloadTemplateRequest({ payload }: DownloadTemplateRequestAction): Generator<any, void, any> {
  const { engineVersionId, side } = payload;

  try {
    // No errorType: it would raise REQUEST_ERROR, the same toast this saga raises below. (A 403 still
    // double-toasts from sendRequest's own branch; that belongs in the helper.)
    const result = yield call(sendRequest, downloadTemplate, { engineVersionId, side });
    const { fileBlobUrl, fileName } = result?.data ?? {};

    if (fileBlobUrl) {
      // The backend names the file, but `Content-Disposition` is not CORS-exposed, so without a
      // fallback the browser names the download after the blob URL — a bare UUID.
      triggerDownload(fileBlobUrl, fileName || `${side}_rate_card_template.csv`);

      return;
    }

    yield put(
      notifications.requestError({
        error: result?.message || t('dash.admin.rateCards.editor.import.errors.template_download_failed'),
      }),
    );
  } catch (error) {
    logger.error(error);
    yield put(
      notifications.requestError({ error: t('dash.admin.rateCards.editor.import.errors.template_download_failed') }),
    );
  } finally {
    yield put(actions.downloadTemplateSettled({ side }));
  }
}

/**
 * Creates the card shell — started by the first upload, or by Create on a card with no files at all.
 * The wizard navigates off `cardId` landing in the store, so a cancelled user is not dragged back.
 */
export function* createRequest({ payload }: CreateRequestAction): Generator<any, void, any> {
  try {
    // No errorType: a 422 carries the field errors the Details step renders; the rest toast below.
    const result = yield call(
      sendRequest,
      createCard,
      {
        data: { rate_card: { name: payload.name, effective_from: payload.effectiveFrom } },
        params: { engine_version_id: payload.versionId },
      },
      undefined,
      undefined,
      undefined,
      { preserveErrorStatus: true },
    );

    if (isSuccess(result) && result.data?.id) {
      yield put(actions.createSuccess({ cardId: result.data.id }));
    } else if (result?.status === RATE_CARD_HTTP.CONFLICT) {
      // The chosen version is no longer active or scheduled, so the refusal belongs under Details where
      // the dropdown is. An unparseable body yields '', which would banner a heading with nothing under it.
      if (result.message) {
        yield put(actions.createError({ validations: { base: [result.message] } }));
      } else {
        yield put(notifications.requestError({ error: t('dash.common.notification.something_went_wrong') }));
        yield put(actions.createError({ validations: null }));
      }
    } else {
      const validations = validationsOf(result);

      if (!validations) {
        yield put(notifications.requestError({ error: t('dash.common.notification.something_went_wrong') }));
      }

      yield put(actions.createError({ validations }));
    }
  } catch (error) {
    logger.error(error);
    yield put(notifications.requestError({ error: t('dash.common.notification.something_went_wrong') }));
    yield put(actions.createError({ validations: null }));
  }
}

export default function* rootSaga() {
  yield all([
    // `takeEvery`: pay and bill are two independent uploads, and `takeLatest` would cancel the first,
    // leaving its panel spinning — a cancelled task dispatches neither outcome. Re-uploads use the token.
    takeEvery(actionTypes.UPLOAD_SIDE_REQUEST, uploadSideRequest),
    // Same reasoning: two sides are two files, and `takeLatest` would silently deliver only one.
    takeEvery(actionTypes.DOWNLOAD_TEMPLATE_REQUEST, downloadTemplateRequest),
    // `takeLatest` never supersedes anything here, and must not: redux-saga cancels the task, not the
    // POST, so a cancelled create would still mint an undeletable card. Nothing dispatches a second one.
    takeLatest(actionTypes.CREATE_REQUEST, createRequest),
  ]);
}
